/**
 * MobileTrainingHome — Mobile shell for /training ( <lg ).
 * Batch 6.2 — presents the same 4 desktop sub-tabs (Overview / Records /
 * Matrix / Courses) using mobile-friendly primitives:
 *   - Horizontally-scrolling KPI strip (existing summary payload)
 *   - Pill sub-tabs with overflow-x-auto (44 px targets)
 *   - Body branches to MobileTrainingRecordsList / MobileTrainingMatrixStaff
 *     for Records + Matrix, and reuses the existing OverviewTab / CoursesTab
 *     for Overview + Courses (they already reflow on narrow widths).
 *
 * No new APIs, no new state, same handlers as desktop.
 * Care workers do NOT see `New Record` or `Import CSV`.
 */
import React from "react";
import {
  GraduationCap, RefreshCw, Upload, Download, Plus, MoreVertical,
  ClipboardList, LayoutGrid, BookOpen, LineChart, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import OverviewTab from "@/components/training/OverviewTab";
import CoursesTab from "@/components/training/CoursesTab";
import MobileTrainingRecordsList from "@/components/mobile/MobileTrainingRecordsList";
import MobileTrainingMatrixStaff from "@/components/mobile/MobileTrainingMatrixStaff";

const TABS = [
  { key: "overview", label: "Overview", Icon: LineChart },
  { key: "records",  label: "Records",  Icon: ClipboardList },
  { key: "matrix",   label: "Matrix",   Icon: LayoutGrid },
  { key: "courses",  label: "Courses",  Icon: BookOpen },
];

function KpiTile({ label, value, sub, tone = "slate", testid }) {
  const toneCls = {
    slate:   "border-slate-200 text-slate-900",
    emerald: "border-emerald-200 text-emerald-800 bg-emerald-50/60",
    amber:   "border-amber-200 text-amber-800 bg-amber-50/60",
    red:     "border-red-200 text-red-800 bg-red-50/60",
    sky:     "border-sky-200 text-sky-800 bg-sky-50/60",
  }[tone] || "border-slate-200 text-slate-900";
  return (
    <div
      data-testid={testid}
      className={`shrink-0 w-[150px] rounded-lg border px-3 py-2 bg-white ${toneCls}`}
    >
      <div className="text-[9px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="font-heading text-lg font-semibold tabular-nums mt-0.5">{value}</div>
      <div className="text-[10px] opacity-70 truncate">{sub}</div>
    </div>
  );
}

export default function MobileTrainingHome({
  tab, setTab,
  summary, actions,
  canManage, refreshTick, onRefresh,
  onNewRecord, onImportCsv, onExportPdf,
  onOpenStaff, onChanged,
}) {
  const s = summary || {};
  const pct = summary && s.total_requirements ? `${(s.compliance_pct ?? 0).toFixed(1)}%` : (summary ? "—" : "…");
  const totalActions = actions?.total || 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-slate-50" data-testid="mobile-training-home">
      {/* Compact header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
            <GraduationCap className="w-4 h-4 text-sky-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Governance</div>
            <h1 className="font-heading text-lg font-semibold text-slate-900 truncate" data-testid="mobile-training-page-title">
              Training &amp; Compliance
            </h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canManage && (
              <Button
                size="sm"
                onClick={onNewRecord}
                data-testid="btn-mobile-training-new-record"
                className="h-11 min-h-[44px] bg-sky-700 hover:bg-sky-800 text-xs px-3"
              >
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="btn-mobile-training-more"
                  className="min-h-[44px] w-11 h-11 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                  aria-label="More actions"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={onRefresh} data-testid="btn-mobile-training-refresh">
                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
                </DropdownMenuItem>
                {canManage && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onImportCsv} data-testid="btn-mobile-training-import-csv">
                      <Upload className="w-3.5 h-3.5 mr-2" /> Import CSV
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={onExportPdf} data-testid="btn-mobile-training-export-pdf">
                  <Download className="w-3.5 h-3.5 mr-2" /> Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* KPI strip — horizontal scroll */}
        <div
          className="mt-3 -mx-4 px-4 overflow-x-auto snap-x snap-mandatory flex gap-2 pb-1"
          data-testid="mobile-training-kpi-strip"
        >
          <KpiTile
            testid="mobile-tc-compliance"
            label="Compliance"
            value={pct}
            sub={summary && s.total_requirements ? `${s.compliant_requirements}/${s.total_requirements} met` : "No requirements"}
            tone={
              !summary || !s.total_requirements ? "slate"
                : s.compliance_pct >= 90 ? "emerald"
                : s.compliance_pct >= 70 ? "amber"
                : "red"
            }
          />
          <KpiTile
            testid="mobile-tc-fully"
            label="Fully Compliant"
            value={summary ? s.fully_compliant : "—"}
            sub={summary ? `of ${s.staff_with_requirements} staff` : ""}
            tone={summary && s.fully_compliant === s.staff_with_requirements && s.staff_with_requirements ? "emerald" : "slate"}
          />
          <KpiTile
            testid="mobile-tc-expiring"
            label="Expiring 30d"
            value={summary ? s.expiring_soon : "—"}
            sub="Approaching renewal"
            tone={s.expiring_soon > 0 ? "amber" : "emerald"}
          />
          <KpiTile
            testid="mobile-tc-expired"
            label="Expired"
            value={summary ? s.expired_or_overdue : "—"}
            sub="Past expiry"
            tone={s.expired_or_overdue > 0 ? "red" : "emerald"}
          />
          <KpiTile
            testid="mobile-tc-gaps"
            label="Mandatory Gaps"
            value={summary ? s.mandatory_gaps : "—"}
            sub="Never completed"
            tone={s.mandatory_gaps > 0 ? "red" : "emerald"}
          />
        </div>
      </div>

      {/* Sub-tab pills */}
      <div className="bg-white border-b border-slate-200 px-4 shrink-0">
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="mobile-training-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.Icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`mobile-training-tab-${t.key}`}
                className={`min-h-[44px] shrink-0 px-3 py-2 text-xs font-medium border-b-2 flex items-center gap-1.5 whitespace-nowrap transition ${
                  active ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Attention banner — only when there are actions to flag */}
      {totalActions > 0 && (
        <div className="px-4 pt-3 shrink-0">
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2"
            data-testid="mobile-training-attention"
          >
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0" />
            <div className="text-xs text-amber-900 min-w-0">
              <div className="font-medium">
                {totalActions} training action{totalActions === 1 ? "" : "s"} require attention
              </div>
              <div className="text-[11px] text-amber-800/80 mt-0.5">
                {(actions?.expired?.length || 0) > 0 && <span className="mr-2">{actions.expired.length} expired</span>}
                {(actions?.expiring?.length || 0) > 0 && <span className="mr-2">· {actions.expiring.length} expiring</span>}
                {(actions?.missing?.length || 0) > 0 && <span>· {actions.missing.length} missing</span>}
              </div>
            </div>
            <button
              onClick={() => setTab("records")}
              data-testid="btn-mobile-training-attention-view"
              className="ml-auto min-h-[44px] shrink-0 text-xs font-medium text-amber-900 underline"
            >
              View
            </button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-4">
          {tab === "overview" && (
            <OverviewTab
              summary={summary}
              actions={actions}
              onOpenStaff={onOpenStaff}
              refreshTick={refreshTick}
            />
          )}
          {tab === "records" && (
            <MobileTrainingRecordsList
              onOpenStaff={onOpenStaff}
              onChanged={onChanged}
              canManage={canManage}
              refreshTick={refreshTick}
            />
          )}
          {tab === "matrix" && (
            <MobileTrainingMatrixStaff
              onOpenStaff={onOpenStaff}
              refreshTick={refreshTick}
            />
          )}
          {tab === "courses" && (
            <CoursesTab
              canManage={canManage}
              onChanged={onChanged}
              refreshTick={refreshTick}
            />
          )}
        </div>
      </div>
    </div>
  );
}
